import React from 'react';
import { Paper, Typography, Grid, Box, Card, CardContent } from '@mui/material';
import { formatDisplayDate, isMeetingUpcoming } from '../../../utils/apiMappers';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG, getStatusChipSx, INACTIVE_STATUS_BG, INACTIVE_STATUS_COLOR } from '../../../constants/statusColors';
import {
  CalendarTodayOutlined as CalendarIcon,
  PersonOutlined as PersonIcon,
  PeopleOutlined as PeopleIcon,
  EventOutlined as EventIcon,
  DescriptionOutlined as DocumentIcon,
  AccessTimeOutlined as FrequencyIcon,
  HowToVoteOutlined as QuorumIcon,
  AccountTreeOutlined as ReportingIcon,
  GavelOutlined as RegulatoryIcon,
} from '@mui/icons-material';

const OverviewTab = ({ committee }) => {
  // Calculate stats
  const totalMembers = committee.members?.length || 0;
  const completedMeetings = committee.meetings?.filter((m) => m.status === 'COMPLETED').length || 0;
  const upcomingMeetings = committee.meetings?.filter(isMeetingUpcoming).length || 0;
  const totalDocuments = committee.documents?.length || 0;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card sx={{ height: '100%', borderRadius: '12px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={600} color={color}>
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  const InfoItem = ({ icon: Icon, label, value, iconBg, iconColor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 24, color: iconColor }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="textSecondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PeopleIcon}
            label="Total Members"
            value={totalMembers}
            color="#1976D2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={EventIcon}
            label="Completed Meetings"
            value={completedMeetings}
            color={ACTIVE_GREEN}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={EventIcon}
            label="Upcoming Meetings"
            value={upcomingMeetings}
            color="#E65100"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DocumentIcon}
            label="Documents"
            value={totalDocuments}
            color="#7B1FA2"
          />
        </Grid>
      </Grid>

      {/* Main Information Sections */}
      <Grid container spacing={3}>
        {/* Committee Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Committee Information
            </Typography>

            <InfoItem
              icon={CalendarIcon}
              label="Created Date"
              value={formatDisplayDate(committee.createdDate)}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />

            <InfoItem
              icon={FrequencyIcon}
              label="Meeting Frequency"
              value={committee.meetingFrequency}
              iconBg={ACTIVE_GREEN_BG}
              iconColor={ACTIVE_GREEN}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: committee.status === 'ACTIVE' ? ACTIVE_GREEN_BG : INACTIVE_STATUS_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PeopleIcon sx={{ fontSize: 24, color: committee.status === 'ACTIVE' ? ACTIVE_GREEN : INACTIVE_STATUS_COLOR }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Status
                </Typography>
                <Box
                  component="span"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '12px',
                    ...getStatusChipSx(committee.status),
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'inline-block',
                    mt: 0.5,
                  }}
                >
                  {committee.status}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Key Members */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Key Members
            </Typography>

            <InfoItem
              icon={PersonIcon}
              label="Chairperson"
              value={committee.members?.find((m) => m.role === 'CHAIRPERSON')?.name || 'Not assigned'}
              iconBg="#FFEBEE"
              iconColor="#D32F2F"
            />

            <InfoItem
              icon={PersonIcon}
              label="Secretary"
              value={committee.members?.find((m) => m.role === 'SECRETARY')?.name || 'Not assigned'}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />

            <InfoItem
              icon={PeopleIcon}
              label="Total Members"
              value={`${totalMembers} members`}
              iconBg="#F5F5F5"
              iconColor="#757575"
            />
          </Paper>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.7 }}>
              {committee.description}
            </Typography>
          </Paper>
        </Grid>

        {/* Quorum Requirements */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Quorum Requirements
            </Typography>

            <InfoItem
              icon={QuorumIcon}
              label="Quorum Type"
              value={committee.quorumType === 'PERCENTAGE' ? 'Percentage of Total Members' : 'Number of Members'}
              iconBg="#FFF3E0"
              iconColor="#E65100"
            />

            {committee.quorumType === 'PERCENTAGE' && (
              <InfoItem
                icon={QuorumIcon}
                label="Required Percentage"
                value={`${committee.quorumPercentage}%`}
                iconBg="#FFF3E0"
                iconColor="#E65100"
              />
            )}

            {committee.quorumType === 'NUMBER' && (
              <InfoItem
                icon={QuorumIcon}
                label="Required Number"
                value={`${committee.quorumNumber} members`}
                iconBg="#FFF3E0"
                iconColor="#E65100"
              />
            )}

            <InfoItem
              icon={PersonIcon}
              label="Chairperson Required for Quorum"
              value={committee.chairpersonRequiredForQuorum ? 'Yes' : 'No'}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />
          </Paper>
        </Grid>

        {/* Governance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Governance
            </Typography>

            <InfoItem
              icon={ReportingIcon}
              label="Reporting Structure"
              value={committee.reportingStructure}
              iconBg={ACTIVE_GREEN_BG}
              iconColor={ACTIVE_GREEN}
            />

            <InfoItem
              icon={RegulatoryIcon}
              label="Regulatory Requirements"
              value={committee.regulatoryRequirements}
              iconBg="#F3E5F5"
              iconColor="#7B1FA2"
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewTab;
